import { cn } from '@/lib/utils'

/**
 * Decorative backdrop for the hero: a fine dot lattice and concentric
 * "assurance" arcs radiating from the top-right, drawn in currentColor so it
 * follows the theme. Purely presentational (aria-hidden).
 */
export function HeroBackdrop({ className }: { className?: string }) {
    return (
        <svg
            aria-hidden
            focusable="false"
            className={cn('pointer-events-none absolute inset-0 size-full text-primary', className)}
            preserveAspectRatio="xMaxYMin slice"
            viewBox="0 0 1200 420"
        >
            <defs>
                <pattern id="home-dots" width="22" height="22" patternUnits="userSpaceOnUse">
                    <circle cx="1.5" cy="1.5" r="1.1" fill="currentColor" opacity="0.18" />
                </pattern>
                <radialGradient id="home-dots-fade" cx="85%" cy="10%" r="70%">
                    <stop offset="0" stopColor="white" stopOpacity="1" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                </radialGradient>
                <mask id="home-dots-mask">
                    <rect width="1200" height="420" fill="url(#home-dots-fade)" />
                </mask>
            </defs>
            <rect width="1200" height="420" fill="url(#home-dots)" mask="url(#home-dots-mask)" />
            <g fill="none" stroke="currentColor" strokeWidth="1.25">
                {[90, 160, 230, 300, 370, 440].map((r, i) => (
                    <circle key={r} cx="1080" cy="40" r={r} opacity={0.22 - i * 0.03} />
                ))}
            </g>
            <circle cx="1080" cy="40" r="7" className="fill-accent-strong" />
            <circle cx="1080" cy="40" r="18" fill="none" className="stroke-accent-strong" strokeWidth="1.5" opacity="0.5" />
        </svg>
    )
}

/** Small shield-and-check illustration shown in the activity panel when there's nothing to resume. */
export function CalmIllustration({ className }: { className?: string }) {
    return (
        <svg aria-hidden focusable="false" viewBox="0 0 220 180" className={cn('text-primary', className)}>
            <g fill="none" stroke="currentColor" strokeWidth="1.25" opacity="0.25">
                <circle cx="110" cy="90" r="52" />
                <circle cx="110" cy="90" r="72" />
                <circle cx="110" cy="90" r="88" strokeDasharray="2 6" />
            </g>
            <path
                d="M110 40 L146 54 V88 C146 112 130 130 110 138 C90 130 74 112 74 88 V54 Z"
                className="fill-primary/10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
            />
            <path d="M94 89 L106 101 L128 78" fill="none" className="stroke-accent-strong" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="182" cy="40" r="4" className="fill-accent-strong" />
            <circle cx="38" cy="132" r="3" fill="currentColor" opacity="0.4" />
        </svg>
    )
}
